import classNames from "classnames";
import { useMemo, useState } from "react";
import { useFormik } from "formik";

import AscTabs from "../../AscTabs/AscTabs";
import AscHeading from "../../AscTypography/AscHeading/AscHeading";
import AscAddModuleFooter from "../AscAddModuleFooter/AscAddModuleFooter";
import AscAddApps from "./AscAddApps/AscAddApps";
import AscAddMovies from "./AscAddMovies/AscAddMovies";
import AscAddPodcasts from "./AscAddPodcasts/AscAddPodcasts";
import AscAddYoutubeVideos from "./AscAddYoutubeVideos/AscAddYoutubeVideos";
import AscAddBookFeedback from "./AscAddBookFeedback/AscAddBookFeedback";
import { feedbackFormValidationSchema } from "./AscAddFeedback.FormValidation";
import styles from "./AscAddFeedback.module.scss";

export interface AscAddFeedbackProps {
  className?: string;
  header: string;
  value: number;
  currentFeedbackObj: any;
  setValue: (value: number) => void;
  handleFeedbackData: any;
  deleteFeedbackBook?: (bookId: string) => Promise<void>;
  deleteFeedbackYoutube?: (youtubeId: string) => Promise<void>;
  deleteFeedbackPodcast?: (podcastId: string) => Promise<void>;
  deleteFeedbackMovie?: (movieId: string) => Promise<void>;
  deleteFeedbackApp?: (appId: string) => Promise<void>;
}

export default function AscAddFeedback({
  className,
  header,
  value,
  currentFeedbackObj,
  setValue,
  handleFeedbackData,
  deleteFeedbackApp,
  deleteFeedbackBook,
  deleteFeedbackMovie,
  deleteFeedbackPodcast,
  deleteFeedbackYoutube,
}: AscAddFeedbackProps) {
  const [currentTab, setCurrentTab] = useState("app");

  const formik = useFormik<Record<string, object>>({
    initialValues: {
      app: currentFeedbackObj?.app || [],
      movie: currentFeedbackObj?.movie || [],
      youtube: currentFeedbackObj?.youtube || [],
      podcast: currentFeedbackObj?.podcast || [],
      book: currentFeedbackObj?.book || [],
    },
    validationSchema: feedbackFormValidationSchema,
    onSubmit: (values: any) => {
      const feedback = {
        app: values.app?.filter(
          (app: Record<"image", File | string>) => !!app?.image
        ),
        movie: values.movie?.filter(
          (movie: Record<"image", File | string>) => !!movie?.image
        ),
        youtube: values.youtube?.filter(
          (youtube: Record<"image", File | string>) => !!youtube?.image
        ),
        podcast: values.podcast?.filter(
          (podcast: Record<"image", File | string>) => !!podcast?.image
        ),
        book: values.book?.filter(
          (book: Record<"image", File | string>) => !!book?.image
        ),
      };
      handleFeedbackData(feedback);
    },
  });

  const touched = useMemo(() => {
    return (
      Object.keys(formik.touched)?.filter(
        (key) => formik.touched?.[key as keyof typeof formik.touched]
      ).length > 0
    );
  }, [formik.touched]);

  const submitAndNext = () => {
    formik.handleSubmit();
    setValue(value + 1);
  };

  return (
    <div
      data-testid="asc-add-feedback"
      className={classNames(styles.ground, className)}
    >
      <div className={styles.topGround}>
        <AscHeading className={styles.header} level={6}>
          {header}
        </AscHeading>
      </div>

      <AscTabs
        type="secondary"
        className={styles.tabsGround}
        tabContentClassName={styles.tabContentClassName}
        navigationClassName={styles.navigationClassName}
        selectedId={currentTab}
        onChange={setCurrentTab}
      >
        <AscTabs.Tab
          className={styles.tabs}
          id={"app"}
          title={"Apps"}
          hasError={Object.keys(formik.errors).includes("app")}
        >
          <AscAddApps formik={formik} deleteFeedbackApp={deleteFeedbackApp} />
        </AscTabs.Tab>
        <AscTabs.Tab
          className={styles.tabs}
          id={"movie"}
          title={"Movies"}
          hasError={Object.keys(formik.errors).includes("movie")}
        >
          <AscAddMovies
            formik={formik}
            deleteFeedbackMovie={deleteFeedbackMovie}
          />
        </AscTabs.Tab>
        <AscTabs.Tab
          className={styles.tabs}
          id={"youtube"}
          title={"Youtube videos"}
          hasError={Object.keys(formik.errors).includes("youtube")}
        >
          <AscAddYoutubeVideos
            formik={formik}
            deleteFeedbackYoutube={deleteFeedbackYoutube}
          />
        </AscTabs.Tab>
        <AscTabs.Tab
          className={styles.tabs}
          id={"podcast"}
          title={"Podcasts"}
          hasError={Object.keys(formik.errors).includes("podcast")}
        >
          <AscAddPodcasts
            formik={formik}
            deleteFeedbackPodcast={deleteFeedbackPodcast}
          />
        </AscTabs.Tab>
        <AscTabs.Tab
          className={styles.tabs}
          id={"book"}
          title={"Books"}
          hasError={Object.keys(formik.errors).includes("book")}
        >
          <AscAddBookFeedback
            formik={formik}
            deleteFeedbackBook={deleteFeedbackBook}
          />
        </AscTabs.Tab>
      </AscTabs>

      <AscAddModuleFooter
        className={styles.footer}
        currentValue={value}
        disabled={touched && !formik.isValid}
        goNext={() => submitAndNext()}
        goBack={() => {
          formik.handleSubmit();
          setValue(value - 1);
        }}
      />
    </div>
  );
}
